import React, { useCallback, useEffect, useRef, useState } from 'react';
import { monitorApi, ActiveCall, LiveStats, TranscriptEntry } from '../../api/monitor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Whisper Modal ────────────────────────────────────────────────────────────
const WhisperModal: React.FC<{
  callSid: string;
  agentName: string;
  onClose: () => void;
}> = ({ callSid, agentName, onClose }) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  const handleInject = async () => {
    if (!text.trim()) return;
    setStatus('sending');
    try {
      await monitorApi.whisper(callSid, text.trim());
      setStatus('done');
      setTimeout(onClose, 1200);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rounded-xl p-6 w-full max-w-md"
        style={{ background: '#13131f', border: '1px solid rgba(99,102,241,0.3)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-base">📞 Whisper to {agentName}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <p className="text-gray-400 text-sm mb-3">What should the agent say next?</p>
        <textarea
          className="w-full rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
          style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', height: 100 }}
          placeholder="e.g. Offer them a 20% discount and ask about their timeline..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={status === 'sending' || status === 'done'}
          autoFocus
        />
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={handleInject}
            disabled={!text.trim() || status === 'sending' || status === 'done'}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
            style={{
              background: status === 'done' ? '#22c55e' : 'rgba(99,102,241,0.8)',
              opacity: (!text.trim() || status === 'sending') ? 0.5 : 1,
              cursor: (!text.trim() || status === 'sending') ? 'not-allowed' : 'pointer',
            }}
          >
            {status === 'sending' ? 'Injecting...' : status === 'done' ? 'Injected ✓' : 'Inject'}
          </button>
          {status === 'error' && (
            <span className="text-red-400 text-sm">Failed to inject. Try again.</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Transcript Panel ─────────────────────────────────────────────────────────
const TranscriptPanel: React.FC<{
  call: ActiveCall;
  transcript: TranscriptEntry[];
  onClose: () => void;
}> = ({ call, transcript, onClose }) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  return (
    <div
      className="flex flex-col h-full"
      style={{ background: '#0d0d15', borderLeft: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div>
          <div className="text-white font-semibold text-sm">{call.agentName}</div>
          <div className="text-gray-500 text-xs mt-0.5">{call.phone}</div>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">&times;</button>
      </div>

      {/* Transcript bubbles */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {transcript.length === 0 ? (
          <div className="text-center text-gray-600 text-sm pt-8">No transcript yet</div>
        ) : (
          transcript.map((entry, idx) => {
            const isAgent = entry.speaker === 'agent';
            return (
              <div key={idx} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
                <div
                  className="max-w-[85%] rounded-xl px-3 py-2"
                  style={{
                    background: isAgent ? 'rgba(99,102,241,0.15)' : 'rgba(63,63,70,0.4)',
                    border: isAgent ? '1px solid rgba(99,102,241,0.2)' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className="text-xs font-medium mb-1" style={{ color: isAgent ? '#818cf8' : '#9ca3af' }}>
                    {isAgent ? call.agentName : 'Caller'}
                  </div>
                  <div className="text-white text-sm leading-relaxed">{entry.text}</div>
                  <div className="text-gray-600 text-[10px] mt-1">
                    {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

// ─── Call Card ─────────────────────────────────────────────────────────────────
const CallCard: React.FC<{
  call: ActiveCall;
  isSelected: boolean;
  onSelect: () => void;
  onWhisper: () => void;
  onEnd: () => void;
}> = ({ call, isSelected, onSelect, onWhisper, onEnd }) => {
  const [elapsed, setElapsed] = useState(call.duration);

  // Tick duration every second
  useEffect(() => {
    setElapsed(call.duration);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [call.callSid, call.duration]);

  const lastEntry = call.transcript[call.transcript.length - 1];
  const preview = lastEntry?.text?.slice(0, 90) || '';

  return (
    <div
      className="rounded-xl p-4 cursor-pointer transition-all duration-150"
      style={{
        background: isSelected ? 'rgba(99,102,241,0.12)' : '#13131f',
        border: isSelected
          ? '1px solid rgba(99,102,241,0.4)'
          : '1px solid rgba(255,255,255,0.07)',
      }}
      onClick={onSelect}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: '#4ade80',
                animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
              }}
            />
            LIVE
          </span>
          <span className="text-gray-300 text-sm font-medium">{call.agentName}</span>
        </div>
        <span className="text-gray-500 text-xs font-mono">{formatDuration(elapsed)}</span>
      </div>

      {/* Phone */}
      <div className="text-gray-400 text-sm mb-2">{call.phone}</div>

      {/* Transcript preview */}
      {preview && (
        <div
          className="text-gray-500 text-xs italic mb-3 line-clamp-2 leading-relaxed"
          style={{ borderLeft: '2px solid rgba(99,102,241,0.3)', paddingLeft: 8 }}
        >
          "{preview}{preview.length >= 90 ? '...' : ''}"
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onWhisper}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all"
          style={{
            background: 'rgba(99,102,241,0.15)',
            border: '1px solid rgba(99,102,241,0.25)',
            color: '#818cf8',
          }}
        >
          📞 Whisper
        </button>
        <button
          onClick={onEnd}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all"
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.2)',
            color: '#f87171',
          }}
        >
          🔴 End Call
        </button>
      </div>
    </div>
  );
};

// ─── Main CallMonitor Component ───────────────────────────────────────────────
export const CallMonitor: React.FC = () => {
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const [stats, setStats] = useState<LiveStats>({ activeCalls: 0, callsInQueue: 0, agentsIdle: 0, callsThisHour: 0 });
  const [selectedCallSid, setSelectedCallSid] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<Record<string, TranscriptEntry[]>>({});
  const [whisperCallSid, setWhisperCallSid] = useState<string | null>(null);
  const [sseStatus, setSseStatus] = useState<'connecting' | 'live' | 'reconnecting'>('connecting');
  const [endingCalls, setEndingCalls] = useState<Set<string>>(new Set());

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Load initial data ──────────────────────────────────────────
  const loadInitialData = useCallback(async () => {
    try {
      const [calls, liveStats] = await Promise.all([
        monitorApi.getActiveCalls(),
        monitorApi.getLiveStats(),
      ]);
      setActiveCalls(calls);
      setStats(liveStats);
    } catch (err) {
      console.error('Failed to load monitor data', err);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ─── SSE connection ─────────────────────────────────────────────
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const token = localStorage.getItem('token');
    const url = `${API_BASE}/api/v1/events/monitor${token ? `?token=${token}` : ''}`;

    // Note: EventSource doesn't support custom headers — use cookie auth.
    // The backend authenticate middleware supports both Bearer token and cookie.
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setSseStatus('live');
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    es.onerror = () => {
      setSseStatus('reconnecting');
      es.close();
      reconnectTimerRef.current = setTimeout(connectSSE, 3000);
    };

    es.addEventListener('call_started', (e) => {
      const data = JSON.parse(e.data);
      setActiveCalls((prev) => {
        if (prev.find((c) => c.callSid === data.callSid)) return prev;
        const newCall: ActiveCall = {
          callSid: data.callSid,
          sessionId: '',
          agentId: data.agentId,
          agentName: data.agentName || 'Agent',
          phone: data.phone,
          duration: 0,
          status: 'in_progress',
          transcript: [],
        };
        return [newCall, ...prev];
      });
    });

    es.addEventListener('call_ended', (e) => {
      const data = JSON.parse(e.data);
      setActiveCalls((prev) => prev.filter((c) => c.callSid !== data.callSid));
      setTranscripts((prev) => {
        const next = { ...prev };
        delete next[data.callSid];
        return next;
      });
      setSelectedCallSid((prev) => (prev === data.callSid ? null : prev));
    });

    es.addEventListener('transcript_update', (e) => {
      const data = JSON.parse(e.data);
      const entry: TranscriptEntry = data.exchange;
      setTranscripts((prev) => ({
        ...prev,
        [data.callSid]: [...(prev[data.callSid] || []), entry],
      }));
      // Also update the call's inline transcript preview
      setActiveCalls((prev) =>
        prev.map((c) =>
          c.callSid === data.callSid
            ? { ...c, transcript: [...c.transcript, entry].slice(-6) }
            : c
        )
      );
    });

    es.addEventListener('stats_update', (e) => {
      const data = JSON.parse(e.data);
      setStats((prev) => ({ ...prev, ...data }));
    });
  }, []);

  useEffect(() => {
    connectSSE();
    return () => {
      eventSourceRef.current?.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connectSSE]);

  // ─── Load full transcript when a call is selected ──────────────
  useEffect(() => {
    if (!selectedCallSid) return;
    monitorApi.getTranscript(selectedCallSid).then((entries) => {
      setTranscripts((prev) => ({ ...prev, [selectedCallSid]: entries }));
    }).catch(() => {});
  }, [selectedCallSid]);

  // ─── Handlers ──────────────────────────────────────────────────
  const handleEndCall = async (callSid: string) => {
    if (endingCalls.has(callSid)) return;
    if (!window.confirm('Terminate this call?')) return;
    setEndingCalls((prev) => { const s = new Set(Array.from(prev)); s.add(callSid); return s; });
    try {
      await monitorApi.endCall(callSid);
      setActiveCalls((prev) => prev.filter((c) => c.callSid !== callSid));
      if (selectedCallSid === callSid) setSelectedCallSid(null);
    } catch {
      alert('Failed to end call.');
    } finally {
      setEndingCalls((prev) => {
        const next = new Set(prev);
        next.delete(callSid);
        return next;
      });
    }
  };

  const selectedCall = activeCalls.find((c) => c.callSid === selectedCallSid) || null;
  const whisperCall = activeCalls.find((c) => c.callSid === whisperCallSid) || null;

  return (
    <div className="flex flex-col min-h-screen" style={{ background: '#0a0a0f' }}>
      {/* ── Header ────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 px-6 py-5"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-white text-xl font-bold">🎛️ Live Monitor</h1>
            {/* SSE status */}
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{
                background: sseStatus === 'live' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
                color: sseStatus === 'live' ? '#4ade80' : '#fbbf24',
                border: `1px solid ${sseStatus === 'live' ? 'rgba(34,197,94,0.2)' : 'rgba(234,179,8,0.2)'}`,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: sseStatus === 'live' ? '#4ade80' : '#fbbf24',
                  animation: sseStatus === 'live' ? 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' : 'none',
                }}
              />
              {sseStatus === 'live' ? '● Live' : '⚠ Reconnecting...'}
            </span>
          </div>

          {/* Live stats */}
          <div className="flex items-center gap-5 text-sm">
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: '#4ade80', animation: 'pulse 2s ease-in-out infinite' }}
              />
              <span className="text-gray-300 font-semibold">{stats.activeCalls}</span>
              <span className="text-gray-500">active</span>
            </div>
            <div className="text-gray-500">·</div>
            <div>
              <span className="text-gray-300 font-semibold">{stats.callsThisHour}</span>
              <span className="text-gray-500 ml-1">this hour</span>
            </div>
            <div className="text-gray-500">·</div>
            <div>
              <span className="text-gray-300 font-semibold">{stats.agentsIdle}</span>
              <span className="text-gray-500 ml-1">agents idle</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Calls grid */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ minWidth: 0 }}
        >
          {activeCalls.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-24">
              <div className="text-5xl mb-4 opacity-30">📵</div>
              <p className="text-gray-500 text-base font-medium">No active calls right now</p>
              <p className="text-gray-600 text-sm mt-2">
                Campaigns and agents will appear here when calls are in progress
              </p>
            </div>
          ) : (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {activeCalls.map((call) => (
                <CallCard
                  key={call.callSid}
                  call={call}
                  isSelected={selectedCallSid === call.callSid}
                  onSelect={() =>
                    setSelectedCallSid((prev) => (prev === call.callSid ? null : call.callSid))
                  }
                  onWhisper={() => setWhisperCallSid(call.callSid)}
                  onEnd={() => handleEndCall(call.callSid)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Transcript panel */}
        {selectedCall && (
          <div
            className="flex-shrink-0 hidden lg:flex flex-col overflow-hidden"
            style={{ width: 380 }}
          >
            <TranscriptPanel
              call={selectedCall}
              transcript={transcripts[selectedCall.callSid] || []}
              onClose={() => setSelectedCallSid(null)}
            />
          </div>
        )}
      </div>

      {/* Whisper modal */}
      {whisperCall && (
        <WhisperModal
          callSid={whisperCall.callSid}
          agentName={whisperCall.agentName}
          onClose={() => setWhisperCallSid(null)}
        />
      )}

      {/* CSS pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .4; }
        }
      `}</style>
    </div>
  );
};

export default CallMonitor;
